import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Coins, Lock, MapPin } from "lucide-react";
import { toast } from "sonner";
import api, { inr, formatApiError } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { Overline } from "../components/Reveal";

export default function Checkout() {
  const { items, subtotal, clearCart } = useCart();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [ccUse, setCcUse] = useState(0);
  const [busy, setBusy] = useState(false);
  const [saveAddr, setSaveAddr] = useState(true);
  const [rules, setRules] = useState(null);
  const [addr, setAddr] = useState({ line1: "", city: "", state: "", pincode: "" });
  const savedAddresses = user?.saved_addresses || [];

  useEffect(() => {
    if (savedAddresses.length > 0 && !addr.line1) setAddr(savedAddresses[0]);
  }, [user]);
  useEffect(() => { api.get("/settings").then((r) => setRules(r.data.redemption_rules)).catch(() => {}); }, []);

  const balance = user?.cloth_cash_balance || 0;
  const rulesMax = rules ? (subtotal < rules.min_order ? 0 : Math.min(rules.max_per_order, subtotal * rules.max_percent / 100)) : subtotal;
  const maxCc = Math.floor(Math.min(balance, rulesMax));
  const total = subtotal - Math.min(ccUse, maxCc);
  const effectiveCc = Math.min(ccUse, maxCc);

  if (items.length === 0) {
    return (
      <div className="py-32 text-center" data-testid="checkout-empty">
        <p className="font-serif text-3xl text-muted-foreground">Nothing to check out.</p>
        <Link to="/shop" className="inline-block mt-8 text-[11px] tracking-[0.25em] uppercase text-wine underline underline-offset-4">Back to shop</Link>
      </div>
    );
  }

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, qty: i.qty, size: i.size })),
        cloth_cash_to_use: effectiveCc,
        origin_url: window.location.origin,
        shipping: addr,
        save_address: saveAddr,
      });
      if (data.free) {
        clearCart();
        await refreshUser();
        navigate(`/payment/success?order=${data.order_id}&free=1`);
      } else {
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };

  const inputCls = "w-full bg-transparent border border-border px-4 py-3.5 text-sm outline-none focus:border-wine transition-colors placeholder:text-muted-foreground/60";

  return (
    <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-14 md:py-20" data-testid="checkout-page">
      <Overline>Almost Yours</Overline>
      <h1 className="font-serif text-5xl sm:text-6xl tracking-tighter mt-3">Checkout.</h1>
      <div className="mt-12 grid lg:grid-cols-[1fr_380px] gap-14">
        <div className="space-y-10">
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-4">Delivery Address</p>
            {savedAddresses.length > 0 && (
              <div className="mb-4 flex flex-wrap gap-2" data-testid="saved-addresses">
                {savedAddresses.map((a) => (
                  <button key={a.id} type="button" onClick={() => setAddr(a)} data-testid={`saved-address-${a.id}`}
                    className={`flex items-center gap-2 border px-4 py-2.5 text-xs transition-colors ${addr.line1 === a.line1 && addr.pincode === a.pincode ? "bg-ink text-ivory border-ink" : "border-border hover:border-ink"}`}>
                    <MapPin size={12} strokeWidth={1.5} /> {a.label} · {a.city || a.pincode}
                  </button>
                ))}
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4" data-testid="shipping-form">
              <input value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} placeholder="Address line" className={`${inputCls} sm:col-span-2`} data-testid="ship-line1" />
              <input value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} placeholder="City" className={inputCls} data-testid="ship-city" />
              <input value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} placeholder="State" className={inputCls} data-testid="ship-state" />
              <input value={addr.pincode} onChange={(e) => setAddr({ ...addr, pincode: e.target.value })} placeholder="PIN code" className={inputCls} data-testid="ship-pincode" />
            </div>
            <label className="mt-4 flex items-center gap-2.5 text-sm text-ink/60 cursor-pointer">
              <input type="checkbox" checked={saveAddr} onChange={(e) => setSaveAddr(e.target.checked)} className="accent-[#4A0E17] w-4 h-4" data-testid="save-address-check" />
              Save this address to my account
            </label>
          </div>
          <div className="border border-gold/50 bg-gold/5 p-6" data-testid="cloth-cash-apply">
            <p className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-gold"><Coins size={14} strokeWidth={1.5} /> Cloth Cash</p>
            <p className="mt-2 text-sm text-ink/60">Available balance: <span className="font-mono" data-testid="cc-balance">{inr(balance)}</span></p>
            {balance > 0 && rules && (
              <>
                {rulesMax <= 0 ? (
                  <p className="mt-3 text-xs text-muted-foreground" data-testid="cc-below-min">Cloth Cash applies on orders of ₹{rules.min_order}+.</p>
                ) : (
                  <div className="mt-4 flex items-center gap-3">
                    <input type="range" min={0} max={maxCc} step={50} value={effectiveCc} onChange={(e) => setCcUse(Number(e.target.value))}
                      className="flex-1 accent-[#4A0E17]" data-testid="cc-slider" />
                    <button onClick={() => setCcUse(maxCc)} data-testid="cc-apply-max" className="text-[10px] tracking-[0.2em] uppercase border border-gold text-gold px-3 py-2 hover:bg-gold hover:text-ink transition-colors">Use Max</button>
                  </div>
                )}
                <p className="mt-2 text-[11px] text-muted-foreground">Up to {rules.max_percent}% of your order (max {inr(rules.max_per_order)}).</p>
              </>
            )}
            {effectiveCc > 0 && <p className="mt-2 font-mono text-sm text-wine" data-testid="cc-applied">− {inr(effectiveCc)} applied</p>}
          </div>
        </div>
        <div className="bg-sand/60 p-8 h-fit sticky top-28" data-testid="order-summary">
          <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Order Summary</p>
          <div className="mt-5 space-y-3">
            {items.map((i) => (
              <div key={i.key} className="flex justify-between text-sm">
                <span className="text-ink/70">{i.name} × {i.qty}</span>
                <span className="font-mono">{inr(i.price * i.qty)}</span>
              </div>
            ))}
          </div>
          <div className="mt-6 pt-5 border-t border-border space-y-2 font-mono text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>{inr(subtotal)}</span></div>
            {effectiveCc > 0 && <div className="flex justify-between text-gold"><span>Cloth Cash</span><span>− {inr(effectiveCc)}</span></div>}
            <div className="flex justify-between text-lg pt-2 border-t border-border"><span>Total</span><span data-testid="order-total">{inr(total)}</span></div>
          </div>
          <button onClick={pay} disabled={busy} data-testid="pay-btn"
            className="mt-8 w-full bg-wine text-ivory py-4 text-[11px] tracking-[0.25em] uppercase flex items-center justify-center gap-3 hover:bg-ink transition-colors disabled:opacity-50">
            <Lock size={13} strokeWidth={1.5} /> {busy ? "Preparing secure checkout…" : total <= 0 ? "Place Order with Cloth Cash" : "Pay Securely with Stripe"}
          </button>
          <p className="mt-4 text-[11px] text-muted-foreground leading-relaxed">Test mode: use card 4242 4242 4242 4242, any future expiry, any CVC.</p>
        </div>
      </div>
    </div>
  );
}
